import * as THREE from "three";
// @ts-ignore
import {default as Ammo} from "ammo.js/builds/ammo";
import {AmmoPhysics} from "@enable3d/ammo-physics";
import {mergeBufferGeometries} from "three/examples/jsm/utils/BufferGeometryUtils.js";
import Avatar from "./Assets/Avatar.js";
import {SAH} from "three-mesh-bvh";
import _ from "lodash";

import {PHYSICS_SHAPE, IPX_PID} from "utils/constants";

export async function _setupPhysics(context) {
  const ammo = await Ammo();

  context.physics = new AmmoPhysics(context.scenePlay);

  context.physics.setGravity(0, -30, 0);

  context.syncObjects = {};
  context.playColliders = [];
  context.scenePlay.updateMatrixWorld();

  const geometries = [];
  // const compressed_geometries = [];

  context.scenePlay.children.forEach(object => {
    traverseSetup(context, object, {needDefaultSetup: isDefaultSetupTarget(object), canCollide: object.userData.canCollide});

    const isDynamic = !object.userData.isKinematic && !object.userData.isMassless;
    const isMovable = isDynamic || object.userData.canScript;

    if (object.userData.canCollide !== false && object.userData.type !== "particleSystem") {
      if (!isMovable || object.name === "StartLocation" || object.userData.type === "ground") {
        object.traverse(child => {
          if (child.geometry) {
            const isInterleavedBufferAttribute = child.geometry.getAttribute("position").isInterleavedBufferAttribute;

            const subGeometry = child.geometry.clone();
            subGeometry.applyMatrix4(child.matrixWorld);
            for (const key in subGeometry.attributes) {
              if (key !== "position") {
                subGeometry.deleteAttribute(key);
              }
            }
            if (!isInterleavedBufferAttribute) geometries.push(subGeometry.toNonIndexed());
            // else compressed_geometries.push(subGeometry.toNonIndexed());
            else {
              // Note: Create individual bvh if object is compressed...
              // It should be revissed...
              createBoundsTree(child);
              context.playColliders.push(child);
            }
          }
        });
      } else if (context.runMode !== "play" || !isDynamic) {
        // runMode="play"일 때(정확히는 멀티플레이 서버에 연결됐을 때) dynamic 오브젝트를 detectCollision 대상에서 제외
        object.traverse(child => {
          if (child.geometry) {
            createBoundsTree(child);
            context.playColliders.push(child);
          }
        });
      }
    }
  });

  if (geometries.length) {
    const mergedMesh = createBvh(geometries);
    context.scenePlay.add(mergedMesh);
    context.playColliders.unshift(mergedMesh);
  }

  // if (compressed_geometries.length) {
  //   const compressed_mergedMesh = createBvh(compressed_geometries, "compressed_geometries");
  //   context.scenePlay.add(compressed_mergedMesh);
  //   context.playColliders.unshift(compressed_mergedMesh);
  // }

  const scene3DTitle = `${context.scenePlay.name}(${context.scenePlay.uuid.substring(0, 3)})`;
  context.scenePlay.stateBuffer = [];
  context.syncObjects[scene3DTitle] = context.scenePlay;

  const sceneGUITitle = `${context.sceneGUI.name}(${context.sceneGUI.uuid.substring(0, 3)})`;
  context.sceneGUI.stateBuffer = [];
  context.syncObjects[sceneGUITitle] = context.sceneGUI;

  context.sceneGUI.traverse(sprite => {
    if (sprite.userData.canScript) {
      const title = sprite.userData.title || `${sprite.name}(${sprite.uuid.substring(0, 3)})`;
      sprite.stateBuffer = [];
      context.syncObjects[title] = sprite;
    }
  });

  // Create a main avatar when the Ammo is ready
  Avatar.models = {};
  context.avatar = new Avatar(context, true);

  try {
    context.fromIpx || context.isGuestMode ? await context.avatar.load() : await context.avatar.load(context.userInfo.avatarInfo[0].id);
    await context.avatar.loadSocketMap();
  } catch (e) {
    console.error(e);
  }

  if (context.versionManager.checkCompatibility.avatar.isCreatedByDefault()) {
    context.avatar.create();
    context.createPlayControls(context.playCamera);
    context.avatar.setDefaultController();
  }
}

const isDefaultSetupTarget = object => {
  return (
    object.userData.type === "ground" ||
    object.name === "StartLocation" ||
    object.userData.type === "mesh" ||
    object.userData.type === "asset" ||
    object.userData.type === "image"
  );
};

const traverseSetup = (context, object, options) => {
  let {needDefaultSetup, canCollide} = options;
  const title = object.userData.title || `${object.name}(${object.uuid.substring(0, 3)})`;
  object.title = title;
  if (object.name !== "StartLocation") object.name = title;
  // [TEMP] skip the setup process for "asset group's children"
  // [TODO] need general setup process & general userData setting for all individual Object3D objects
  const isBaseObject = !!object.userData.type;
  if (isBaseObject) {
    setupBaseObjectProps(context, object);
    needDefaultSetup = isDefaultSetupTarget(object);
    canCollide = object.userData.canCollide;
  }
  if (needDefaultSetup) setupDefaultProps(object);
  if (canCollide === false) object.layers.set(0);
  handleTransparentObject(object);

  object.children.forEach(child => traverseSetup(context, child, {needDefaultSetup, canCollide}));
};

const setupBaseObjectProps = (context, object) => {
  if (object.name === "StartLocation") context.startLocation = object;

  const isDynamic = !object.userData.isKinematic && !object.userData.isMassless;
  const isMovable = isDynamic || object.userData.canScript;

  if (isMovable) {
    addBodyToObject(object, context.physics);
    handleCollisionFlag(object);
  }

  if (context.runMode === "play" && isMovable) {
    object.stateBuffer = [];
    context.syncObjects[object.title] = object;
    // runMode="play"일 때(정확히는 멀티플레이 서버에 연결됐을 때) dynamic 오브젝트를 kinematic으로 변경
    if (isDynamic && object.body) {
      object.body.setCollisionFlags(2);
    }
  }
};

const setupDefaultProps = object => {
  // these(layers.set(1) & doubleside) are default settings, however it is for projects who are created before these settings become default.
  object.layers.set(1);
  if (object.type === "Mesh") {
    object.material.side = THREE.DoubleSide;
    // TEMP - need to check image source alpha
    if (object.userData.type === "image") {
      object.material.transparent = true;
    } else {
      object.material.transparent = object.material.opacity !== 1;
    }
  }
};

const addBodyToObject = (object, physics) => {
  if (object.userData.type === "ground") {
    physics.add.existing(object, {mass: 0});
    // object.body.setCollisionFlags(2) // kinematic
    object.body.setFriction(object.userData.friction);
  } else if (object.type === "Mesh") {
    if (object.userData.isMassless === true) {
      physics.add.existing(object, {mass: 0});
    } else {
      let mass = 1;
      if (object.userData.mass) mass = object.userData.mass;
      physics.add.existing(object, {mass});
    }
    // object.body.setFriction(object.userData.friction);
    object.body.setFriction(1);
  } else if (object.type === "Group") {
    let mass = 1;
    if (object.userData.isMassless === true) {
      mass = 0;
    } else if (object.userData.mass !== undefined) {
      mass = object.userData.mass;
    }
    if (object.userData.bodyType !== undefined) {
      if (object.userData.bodyType === "compound") {
        // TODO - compound object inner object position problem
      } else if (object.userData.bodyType === "box") {
        physics.add.existing(object, {
          shape: object.userData.bodyType,
          width: object.userData.width,
          height: object.userData.height,
          depth: object.userData.depth,
          mass,
          offset: {y: 0},
        });
      } else if (object.userData.bodyType === "sphere") {
        physics.add.existing(object, {
          shape: object.userData.bodyType,
          radius: object.userData.radius,
          mass,
          offset: {y: 0},
        });
      } else {
        physics.add.existing(object, {
          shape: object.userData.bodyType,
          mass,
          offset: {y: 0},
        });
      }
    } else if (object.userData.width !== undefined) {
      // for old projects
      physics.add.existing(object, {
        shape: PHYSICS_SHAPE.COMPOUND,
        compound: [
          {
            shape: "box",
            width: object.userData.width,
            height: object.userData.height,
            depth: object.userData.depth,
          },
        ],
        mass,
        offset: {y: 0},
      });
    }
  }
};

const handleTransparentObject = object => {
  if (object.material?.transparent === true) {
    object.renderOrder = 1;
    object.material.depthWrite = false;
  }
};

const handleCollisionFlag = object => {
  if (object.body === undefined) return;

  if (object.userData.canCollide === false) object.body.setCollisionFlags(6); // ghost kinematic
  else if (object.userData.isKinematic === true) object.body.setCollisionFlags(2); // kinematic
};

const createBoundsTree = object => {
  if (!object.geometry) return;
  object.geometry.computeBoundsTree({
    maxDepth: 20,
    // maxLeafTris: 10,
    strategy: parseFloat(SAH),
  });
  object.geometry.boundsTree.splitStrategy = SAH;
};

const createBvh = (geometries, name) => {
  const mergedGeometry = mergeBufferGeometries(geometries);
  mergedGeometry.computeBoundsTree({
    maxDepth: 30,
    strategy: parseFloat(SAH),
  });
  mergedGeometry.boundsTree.splitStrategy = SAH;

  const mergedMesh = new THREE.Mesh(mergedGeometry);
  mergedMesh.name = name || "bvhCollider";
  mergedMesh.material.wireframe = true;
  mergedMesh.material.side = THREE.DoubleSide;
  mergedMesh.visible = false;
  mergedMesh.layers.set(1);

  return mergedMesh;
};
